import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import NotificationBell from './NotificationBell';
import ConfirmDialog from './ConfirmDialog';
import { getInitials } from '../utils';
import { changePassword } from '../api';

const icons = {
  dashboard: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
  clock: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  team: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  screenshot: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  activity: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  report: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  employees: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  settings: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93l-1.41 1.41M4.93 4.93l1.41 1.41M19.07 19.07l-1.41-1.41M4.93 19.07l1.41-1.41M21 12h-2M5 12H3M12 21v-2M12 5V3"/></svg>,
  billing: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
};

// role: 'admin' | 'manager' | 'employee'
const getNavItems = (role) => {
  const items = [
    { key: 'dashboard', label: 'Dashboard', icon: 'dashboard', roles: ['admin', 'manager', 'employee'] },
    { key: 'clock', label: 'Time Tracker', icon: 'clock', roles: ['admin', 'manager', 'employee'] },
    { key: 'team', label: 'Team View', icon: 'team', roles: ['admin', 'manager'] },
    { key: 'employees', label: 'Employees', icon: 'employees', roles: ['admin'] },
    { key: 'screenshots', label: 'Screenshots', icon: 'screenshot', roles: ['admin', 'manager', 'employee'] },
    { key: 'activity', label: 'Activity', icon: 'activity', roles: ['admin', 'manager', 'employee'] },
    { key: 'reports', label: 'Reports', icon: 'report', roles: ['admin', 'manager', 'employee'] },
    { key: 'billing', label: 'Billing', icon: 'billing', roles: ['admin'] },
  ];
  return items.filter(i => i.roles.includes(role));
};

export default function Sidebar({ page, setPage, branding, isCapturing, activeEntryId }) {
  const { user, logout, isAdmin } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState('');

  const handleLogoutClick = async () => {
    // Refresh activeEntryId right now so the dialog shows the correct state
    if (window.__refreshActiveEntry) await window.__refreshActiveEntry();
    setShowLogoutConfirm(true);
  };
  const sidebarBg = branding?.sidebar_color || '#1e1b4b';
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  const logoUrl = branding?.company_logo ? `${API_BASE}${branding.company_logo}` : null;
  const companyName = branding?.company_name || 'HRTrack';

  // Close sidebar on page change (mobile)
  useEffect(() => { setMobileOpen(false); }, [page]);

  return (
    <>
      {/* Hamburger button — only visible on mobile via CSS */}
      <button
        className="sidebar-toggle"
        onClick={() => setMobileOpen(o => !o)}
        aria-label="Toggle menu"
      >
        <svg width="20" height="20" fill="none" stroke="#fff" strokeWidth="2" viewBox="0 0 24 24">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>

      {/* Overlay — closes sidebar on tap outside (mobile) */}
      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

    <div className={`sidebar${mobileOpen ? ' open' : ''}`} style={{ background: sidebarBg }}>
      <div className="sidebar-logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {logoUrl
            ? <img src={logoUrl} alt="logo" style={{ height: 28, maxWidth: 120, objectFit: 'contain' }} />
            : <span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#fff', letterSpacing: '-0.3px' }}>
                {companyName}
              </span>
          }
        </div>
        <NotificationBell />
      </div>
      <nav>
        {getNavItems(user?.role || 'employee').map(item => (
          <button
            key={item.key}
            className={`nav-item${page === item.key ? ' active' : ''}`}
            onClick={() => setPage(item.key)}
          >
            {icons[item.icon]}
            {item.label}
          </button>
        ))}

        {isAdmin && (
          <>
            <div style={{ margin: '8px 16px', borderTop: '1px solid rgba(255,255,255,.1)' }} />
            <button
              className={`nav-item${page === 'settings' ? ' active' : ''}`}
              onClick={() => setPage('settings')}
            >
              {icons.settings}
              Settings
            </button>
          </>
        )}
      </nav>

      {isCapturing && (
        <div style={{ margin: '0 12px 8px', padding: '6px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block', animation: 'pulse 1.5s infinite' }} />
          <span style={{ color: '#fca5a5', fontSize: '0.75rem', fontWeight: 600 }}>Screen Recording</span>
        </div>
      )}

      {user && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,.1)' }}>
          <button onClick={() => setPage('profile')} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', padding: '4px 0', borderRadius: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>
              {getInitials(user.employee?.name || user.username)}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ color: '#fff', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.employee?.name || user.username}
              </div>
              <div style={{ color: '#6366f1', fontSize: '0.7rem' }}>{user.role} · Edit profile</div>
            </div>
          </button>
          <button onClick={() => { setPwForm({ current: '', next: '', confirm: '' }); setPwMsg(''); setShowChangePw(true); }} className="nav-item" style={{ padding: '6px 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', width: '100%' }}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Change Password
          </button>
          <button onClick={handleLogoutClick} className="nav-item" style={{ padding: '6px 0', fontSize: '0.8rem', color: '#f87171', width: '100%' }}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Sign Out
          </button>
          {/* If clocked in — block sign-out and prompt to clock out first */}
          <ConfirmDialog
            open={showLogoutConfirm && !!activeEntryId}
            icon="⏱️"
            title="You're Still Clocked In"
            message="Please clock out before signing out. Your session is still active and time is being tracked."
            confirmLabel="Go to Time Tracker"
            confirmClass="btn-primary"
            onConfirm={() => { setShowLogoutConfirm(false); setPage('clock'); }}
            onCancel={() => setShowLogoutConfirm(false)}
          />
          {/* Normal sign-out confirm — only when not clocked in */}
          <ConfirmDialog
            open={showLogoutConfirm && !activeEntryId}
            icon="👋"
            title="Sign Out"
            message="Are you sure you want to sign out?"
            confirmLabel="Sign Out"
            confirmClass="btn-danger"
            onConfirm={() => { setShowLogoutConfirm(false); logout(); }}
            onCancel={() => setShowLogoutConfirm(false)}
          />
        </div>
      )}
    </div>
      {showChangePw && (
        <div className="modal-overlay" onClick={() => setShowChangePw(false)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">Change Password</div>
            <form onSubmit={async e => {
              e.preventDefault();
              setPwMsg('');
              if (pwForm.next !== pwForm.confirm) { setPwMsg('New passwords do not match'); return; }
              if (pwForm.next.length < 6) { setPwMsg('Password must be at least 6 characters'); return; }
              try {
                await changePassword(pwForm.current, pwForm.next);
                setPwMsg('success');
                setTimeout(() => setShowChangePw(false), 1200);
              } catch (err) {
                setPwMsg(err.response?.data?.error || 'Failed to change password');
              }
            }}>
              <div className="form-group"><label>Current Password</label><input type="password" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} required autoFocus /></div>
              <div className="form-group"><label>New Password</label><input type="password" value={pwForm.next} onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} required /></div>
              <div className="form-group"><label>Confirm New Password</label><input type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} required /></div>
              {pwMsg && (
                <div style={{ fontSize: '0.85rem', marginBottom: 12, padding: '8px 12px', borderRadius: 8,
                  background: pwMsg === 'success' ? '#f0fdf4' : '#fef2f2',
                  color: pwMsg === 'success' ? '#16a34a' : '#ef4444' }}>
                  {pwMsg === 'success' ? 'Password changed successfully!' : pwMsg}
                </div>
              )}
              <div className="flex gap-8" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowChangePw(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Change Password</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
